import { useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { IconButton, Typography } from 'shared/ui'
import { formatBytesToNumberWithType } from 'shared/lib'
import { ImageCard } from 'entities/Attachment/ui/ImageCard/ImageCard'
import { VideoCard } from 'entities/Attachment/ui/VideoCard/VideoCard'
import { Attachment } from 'entities/Attachment/model/Attachment'

import styles from './styles.module.scss'

type IUploadedFileCardProps = {
  type: 'image' | 'video'
  onRemove: () => void
  file?: File
  url?: string
  loading?: boolean
  previewClassName?: string
  imageClassName?: string
}

export const UploadedFileCard = observer(
  ({
    file,
    url,
    type,
    loading = false,
    onRemove,
    previewClassName,
    imageClassName,
  }: IUploadedFileCardProps) => {
    const objectUrl = useMemo(() => (!url && file ? URL.createObjectURL(file) : ''), [url, file])

    useEffect(
      () => () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl)
      },
      [objectUrl]
    )

    const name = file?.name ?? url?.split('/').pop() ?? ''

    const attachment = useMemo(() => {
      const source = url || objectUrl
      return source ? new Attachment({ imageUrl: source }) : undefined
    }, [url, objectUrl])

    return (
      <div className={styles.card}>
        <div className={styles.info}>
          <div className={classNames(styles.preview, previewClassName)}>
            {type === 'image' ? (
              <ImageCard
                fullWidth
                attachment={attachment}
                loading={loading}
                className={imageClassName}
              />
            ) : (
              <VideoCard square noDuration attachment={attachment} loading={loading} />
            )}
          </div>
          <div className={styles.meta}>
            <Typography
              ariaLabel='UploadedFileCard_name'
              variant='body-md-regular'
              color='var(--content-primary-primary)'
              ellipsis
            >
              {name}
            </Typography>
            {file && (
              <Typography
                ariaLabel='UploadedFileCard_size'
                variant='body-sm-regular'
                color='var(--content-primary-tertiary)'
              >
                {formatBytesToNumberWithType(file.size)}
              </Typography>
            )}
          </div>
        </div>
        <IconButton
          icon='close'
          ariaLabel='delete_file'
          size='small'
          variant='icon'
          color='tertiary'
          onClick={onRemove}
        />
      </div>
    )
  }
)
